// What the review view (/r/{id}, RFC-043 "Recordings") derives from a
// recording: its events as the timeline holds them, annotation threads by
// anchor, and the lines of a diff. Pure functions, tested without a DOM.
import type { Annotation, Recording, ReviewRequest } from "./api";
import type { EventBody } from "./protocol";
import type { SeqEvent } from "./timeline";

/**
 * Who was asked to review a recording and has not come to review it yet —
 * waiting, expired or failed — beside its reviewers, who are listed already
 * (byj-xgt). One made a reviewer though their mail failed is a reviewer, and
 * is not listed or counted again.
 */
export function notYetReviewing(r: Pick<Recording, "review_requests">): ReviewRequest[] {
  return (r.review_requests ?? []).filter((q) => !q.reviewing && q.state !== "joined" && q.state !== "done");
}

/** Who was asked and not reached — the mail failed or the invite ran out — reviewing already or not. */
export function notReached(r: Pick<Recording, "review_requests">): ReviewRequest[] {
  return (r.review_requests ?? []).filter((q) => q.state === "failed" || q.state === "expired");
}

/**
 * A recording's events as the live timeline holds them: an event's seq is
 * its index in the recording, which is what an annotation's seq_ref names.
 */
export function recordedEvents(events: readonly EventBody[]): SeqEvent[] {
  return events.map((body, i) => ({ seq: i, ts: "", body }));
}

/** A thread: its first annotation and the replies to it, oldest first. */
export interface Thread {
  root: Annotation;
  replies: Annotation[];
}

const byTime = (a: Annotation, b: Annotation) => a.created_at.localeCompare(b.created_at);

/**
 * Group annotations into threads. A reply whose thread is not there (it
 * should not happen) is shown as a thread of its own rather than lost.
 */
export function threads(as: readonly Annotation[]): Thread[] {
  const roots = new Map<string, Thread>();
  for (const a of [...as].sort(byTime)) {
    if (!a.parent_id) roots.set(a.id, { root: a, replies: [] });
  }
  for (const a of [...as].sort(byTime)) {
    if (!a.parent_id) continue;
    const t = roots.get(a.parent_id);
    if (t) t.replies.push(a);
    else roots.set(a.id, { root: a, replies: [] });
  }
  return [...roots.values()].sort((a, b) => a.root.seq_ref - b.root.seq_ref || byTime(a.root, b.root));
}

/** Where a thread is: an event, or a line of a diff in one. */
export function anchorKey(seq: number, anchor: string): string {
  return `${seq}|${anchor}`;
}

export function threadsByAnchor(ts: readonly Thread[]): Map<string, Thread[]> {
  const m = new Map<string, Thread[]>();
  for (const t of ts) {
    const k = anchorKey(t.root.seq_ref, t.root.anchor);
    m.set(k, [...(m.get(k) ?? []), t]);
  }
  return m;
}

/** Threads nobody has resolved, in the recording's order. */
export function openThreads(ts: readonly Thread[]): Thread[] {
  return ts.filter((t) => !t.root.resolved);
}

/** Add or replace one annotation, as the relay answered it. */
export function upsert(as: readonly Annotation[], a: Annotation): Annotation[] {
  const i = as.findIndex((x) => x.id === a.id);
  if (i < 0) return [...as, a];
  const out = [...as];
  out[i] = a;
  return out;
}

// ---- diffs -------------------------------------------------------------------------

export interface DiffLine {
  /** The file the line belongs to, from the diff's headers. */
  file: string;
  /** The line's 1-based position in the event's text: unique in the event, and stable. */
  line: number;
  kind: "add" | "del" | "ctx" | "hunk" | "meta";
  text: string;
}

/** An annotation anchor on a diff line: "diff:<file>:<line>". */
export function diffAnchor(file: string, line: number): string {
  return `diff:${file}:${line}`;
}

const headerFile = (s: string) => s.replace(/^[ab]\//, "").replace(/\t.*$/, "");

/**
 * The lines of a unified diff in a text, or null when it holds none (no
 * `diff --git`, no `--- ` / `+++ ` pair, no `@@` hunk).
 */
export function parseDiff(text: string | undefined): DiffLine[] | null {
  if (!text) return null;
  const lines = text.split("\n");
  const isDiff = lines.some(
    (l, i) => l.startsWith("diff --git ") || (l.startsWith("--- ") && lines[i + 1]?.startsWith("+++ ")) || /^@@ -\d/.test(l),
  );
  if (!isDiff) return null;
  let file = "diff";
  let inHunk = false;
  return lines.map((l, i) => {
    let kind: DiffLine["kind"];
    const git = /^diff --git a\/(\S+) b\/(\S+)/.exec(l);
    if (git) {
      file = git[2]!;
      inHunk = false;
      kind = "meta";
    } else if (l.startsWith("+++ ") && !inHunk) {
      const f = l.slice(4).trim();
      if (f !== "/dev/null") file = headerFile(f);
      kind = "meta";
    } else if (l.startsWith("--- ") && !inHunk) {
      const f = l.slice(4).trim();
      if (f !== "/dev/null") file = headerFile(f);
      kind = "meta";
    } else if (l.startsWith("@@")) {
      inHunk = true;
      kind = "hunk";
    } else if (inHunk && l.startsWith("+")) {
      kind = "add";
    } else if (inHunk && l.startsWith("-")) {
      kind = "del";
    } else if (inHunk && (l.startsWith(" ") || l === "")) {
      kind = "ctx";
    } else {
      inHunk = false;
      kind = "meta";
    }
    return { file, line: i + 1, kind, text: l };
  });
}

/**
 * A cast's markers, in order (asciicast v2 "m" events; byj-1wv.5.7): a
 * turn's start is "turn N". `turnMarker(n)` is the index of turn n's own
 * marker, and -1 when the cast has none: a turn it did not see begin is
 * not given another's place (byj-1wv.51.4).
 */
export function castMarkers(cast: string): { labels: string[]; turnMarker(turn: number): number } {
  const labels: string[] = [];
  for (const line of cast.split("\n").slice(1)) {
    if (!line.includes('"m"')) continue;
    try {
      const e = JSON.parse(line) as unknown[];
      if (e[1] === "m" && typeof e[2] === "string") labels.push(e[2]);
    } catch {
      // a line that does not read is skipped, as the player skips it
    }
  }
  return { labels, turnMarker: (turn: number) => labels.indexOf(`turn ${turn}`) };
}

/**
 * The turns a cast marks as they began: the ones a ▶ screen is offered for
 * (byj-1wv.51.4). The sidecar marks only a turn that begins while it
 * records, so a turn under way when recording was turned on — or one while
 * it was off — has no place on the screen to go to, and is offered none.
 */
export function castTurns(cast: string): Set<number> {
  const out = new Set<number>();
  for (const l of castMarkers(cast).labels) {
    const m = /^turn (\d+)$/.exec(l);
    if (m) out.add(Number(m[1]));
  }
  return out;
}
